import { Col, Form, Input, Row, Select } from "antd";
import { useForm } from "antd/lib/form/Form";
import _ from "lodash";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { EntGrossProductCollection } from "../../../../models/organic/grossProductCollection";
import { DraggableModal } from "../../../../components/DraggableModal";
import { SelectProductType, SelectTerritorialUnits, SelectUnitMeasurement } from "../../../../components/selects";
import GeneralSelect from "../../../../components/selects/general/GeneralSelect";
import { EntInitialFilter } from "../../../../models/filters/initialFilter";
import { EntBasicHB } from "../../../../models/handBooks/basicHB";
import { useLazyGetFarmCategoryListQuery } from "../../../../redux/api/handBooksApi/organic/farmCategoryApi";

enum FormField {
  id,
  year,
  regionId,
  farmCategoryId,
  productTypeId,
  unitMeasurementId,
  amount
}

interface IProps {
  show: boolean;
  item: EntGrossProductCollection;
  onHide: () => void;
  onSubmit: (pos: EntGrossProductCollection) => Promise<boolean>;
}

export const AddGrossProductCollectionModal = (props: IProps) => {
  const { t } = useTranslation();
  const [form] = useForm();
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
	if (props.show) setFieldsFromEntity();
  }, [props.show]);

  const setFieldsFromEntity = (): void => {
	form.setFields([
	  { name: FormField.year, value: props.item.year },
	  { name: FormField.regionId, value: props.item.region?.id },
	  { name: FormField.farmCategoryId, value: props.item.farmCategory?.id },
	  { name: FormField.productTypeId, value: props.item.productType?.id },
	  { name: FormField.unitMeasurementId, value: props.item.unitMeasurement?.id },
	  { name: FormField.amount, value: props.item.amount },
	]);
  };

  const setEntityFromFields = (): EntGrossProductCollection => {
	let res = _.cloneDeep(props.item);
	res.year = form.getFieldValue(FormField.year);
	res.regionId = form.getFieldValue(FormField.regionId);
	res.farmCategoryId = form.getFieldValue(FormField.farmCategoryId);
	res.productTypeId = form.getFieldValue(FormField.productTypeId);
	res.unitMeasurementId = form.getFieldValue(FormField.unitMeasurementId);
	res.amount = form.getFieldValue(FormField.amount);
	return res;
  }

  const handleSubmit = async () => {
	setLoading(true);
	try {
	  await form.validateFields();
	  const data = setEntityFromFields();
	  const result = await props.onSubmit(data);
	  if (result) props.onHide()
	} finally {
	  setLoading(false);
	}
  }

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, index) => currentYear - index);

  return (
	<>
	  <DraggableModal
		title={ props.item.id === null ? t('general.add') : t('general.edit') }
		visible={ props.show }
		onCancel={ props.onHide }
		onSubmit={ handleSubmit }
		submitLoading={ loading }
		width={ 800 }
	  >
		<Form
		  initialValues={ { remember: true } }
		  autoComplete="off"
		  layout="vertical"
		  form={ form }
		>
		  <Row style={ { margin: 0 } } gutter={ 16 }>
			<Col className="gutter-row" span={ 12 }>
			  <Form.Item
				label={ t('year') }
				name={ FormField.year }
				rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			  >
				<Select
				  placeholder={ t("msg.selectPlaceholder") }
				  allowClear
				>
				  { years.map((year) => (
					<Select.Option key={ year } value={ year }>
					  { year }
					</Select.Option>
				  )) }
				</Select>
			  </Form.Item>
			  <Form.Item
				label={ t('Регион') }
				name={ FormField.regionId }
				rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			  >
				<SelectTerritorialUnits type={ ['REGION'] }/>
			  </Form.Item>
			  <Form.Item
				label={ t('Категория ферм') }
				name={ FormField.farmCategoryId }
				rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			  >
				<GeneralSelect<EntBasicHB, EntInitialFilter>
				  queryHook={ useLazyGetFarmCategoryListQuery }
				  initialFilter={ new EntInitialFilter() }
				/>
			  </Form.Item>
			</Col>
			<Col className="gutter-row" span={ 12 }>
			  <Form.Item
				label={ t('Тип продукта') }
				name={ FormField.productTypeId }
				rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			  >
				<SelectProductType/>
			  </Form.Item>
			  <Form.Item
				label={ t('Единица измерения') }
				name={ FormField.unitMeasurementId }
				rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			  >
				<SelectUnitMeasurement/>
			  </Form.Item>
			  <Form.Item
				label={ t('Количество') }
				name={ FormField.amount }
				rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			  >
				<Input type="number"/>
			  </Form.Item>
			</Col>
		  </Row>
		</Form>
	  </DraggableModal>
	</>
  )
}